import {
  deliverCameraBroadcast,
  discardCameraBroadcastRecording,
  fetchCameraBroadcastDelivery,
  fetchCameraBroadcastReadiness,
  saveCameraBroadcastRecording,
  type CameraBroadcastStatus,
} from '@/shared/api/camera-broadcast-client';
import type { IoTFieldDataContextValue } from '@/domains/iot-v2/provider';

export type BroadcastCamera = { uid: string; name: string };
export type RecordedBroadcastStatus = CameraBroadcastStatus | 'available' | 'indeterminate' | 'queued' | 'sent' | 'skipped' | 'unavailable';
export type RecordedBroadcastResult = BroadcastCamera & { status: RecordedBroadcastStatus; message: string };

/** 정식 V1·V2 운영 화면에서만 송신하며 운영 정보 미확정과 V2 시험 보기는 차단합니다. */
export function isCameraBroadcastAllowed(field: Pick<IoTFieldDataContextValue, 'provider' | 'activePhase' | 'loadStatus' | 'canWrite'>): boolean {
  return field.provider === 'V2' && field.loadStatus === 'READY' && field.canWrite && field.activePhase === 'V2_ACTIVE';
}

const deliveryApi = {
  readiness: fetchCameraBroadcastReadiness,
  upload: saveCameraBroadcastRecording,
  deliver: deliverCameraBroadcast,
  poll: fetchCameraBroadcastDelivery,
  discard: discardCameraBroadcastRecording,
  wait: (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms)),
};

function statusMessage(status: CameraBroadcastStatus): string {
  if (status === 'preparing') return '음원 변환 준비 중';
  if (status === 'accepted') return 'Media Server 접수 완료';
  if (status === 'processing') return '방송 처리 중';
  if (status === 'streaming') return '음성 전파 중';
  if (status === 'completed') return '전송 완료';
  if (status === 'unknown') return '결과 확인 불가';
  if (status === 'stopped') return '전송 중지';
  return status === 'cancelled' ? '전송 취소' : '전송 실패';
}

/** 준비 상태를 일괄 확인하고 가능한 CCTV만 병렬로 등록한 뒤 종결 상태까지 조회합니다. */
export async function sendRecordedBroadcast(
  blob: Blob,
  cameras: BroadcastCamera[],
  onUpdate: (result: RecordedBroadcastResult) => void,
  isCancelled: () => boolean,
  onAvailable: (cameras: BroadcastCamera[]) => void = () => undefined,
  api = deliveryApi
): Promise<RecordedBroadcastResult[]> {
  const latest = new Map<string, RecordedBroadcastResult>();
  const update = (result: RecordedBroadcastResult) => {
    latest.set(result.uid, result);
    onUpdate(result);
  };

  // 현장·권한이 바뀐 경우 준비 상태 조회를 포함한 후속 요청을 시작하지 않습니다.
  if (isCancelled()) return [];
  const readiness = await api.readiness(cameras.map(camera => camera.uid));
  if (isCancelled()) return [];
  if (!readiness.ok) {
    cameras.forEach(camera => update({ ...camera, status: 'indeterminate', message: readiness.error.message }));
    return cameras.map(camera => latest.get(camera.uid)!);
  }

  const readinessByCamera = new Map(readiness.data.items.map(item => [item.camera_uid, item]));
  const availableCameras: BroadcastCamera[] = [];
  for (const camera of cameras) {
    const item = readinessByCamera.get(camera.uid);
    if (!item || item.state === 'indeterminate') {
      update({ ...camera, status: 'indeterminate', message: item?.message ?? '방송 준비 상태를 확인하지 못했습니다.' });
    } else if (item.state === 'unavailable') {
      update({ ...camera, status: 'unavailable', message: item.message ?? '현재 방송할 수 없는 상태입니다.' });
    } else {
      availableCameras.push(camera);
      update({ ...camera, status: 'available', message: '전송 가능' });
    }
  }

  if (!availableCameras.length || isCancelled()) return cameras.map(camera => latest.get(camera.uid)!);
  onAvailable(availableCameras);

  await Promise.all(
    availableCameras.map(async camera => {
      let recordingId: string | null = null;
      let deliveryPending = false;
      try {
        if (isCancelled()) return;
        update({ ...camera, status: 'preparing', message: '녹음 파일 등록 중' });
        const upload = await api.upload(camera.uid, blob);
        if (!upload.ok) throw new Error(upload.error.message);
        recordingId = upload.data.draft?.recording_id ?? null;
        if (!recordingId) throw new Error('녹음 파일 저장에 실패했습니다.');
        if (isCancelled()) return;
        deliveryPending = true;
        const sent = await api.deliver(camera.uid, { source: 'DRAFT' });
        if (!sent.ok) throw new Error(sent.error.message);
        let delivery = sent.data;
        update({ ...camera, status: delivery.status, message: statusMessage(delivery.status) });
        while (!delivery.terminal) {
          await api.wait(delivery.poll_after_ms ?? 1000);
          if (isCancelled()) return;
          const polled = await api.poll(camera.uid, delivery.request_uid);
          if (!polled.ok) throw new Error(polled.error.message);
          delivery = { ...delivery, ...polled.data };
          update({ ...camera, status: delivery.status, message: statusMessage(delivery.status) });
        }
        deliveryPending = delivery.status === 'unknown';
      } catch (error) {
        update({
          ...camera,
          status: deliveryPending ? 'unknown' : 'failed',
          message: error instanceof Error ? error.message : '전송 실패',
        });
      } finally {
        if (recordingId && !deliveryPending) void api.discard(camera.uid, recordingId).catch(() => undefined);
      }
    })
  );

  return cameras.map(camera => latest.get(camera.uid)!);
}
